import { Injectable } from "@angular/core";
import { ApplicationCategory } from "@app/core/model/applications/application-category";
import { IAppSession } from "@app/core/model/appSessions/appSession";
import { BaseService } from "@app/core/services/base.service";
import { PropertyName } from "@app/core/model/properties/property-name";
import { InstrumentPlatforms, InstrumentTypes } from "@app/core/model/runs/instrument";
import { PropertyService } from "@app/core/services/property/property.service";
import { StringUtilities } from "@app/core/utilities/string-utilities";
import { FilePreviewModalComponent } from "@app/shared/modals/file-preview-modal/file-preview-modal.component";
import { IFilePreviewModalInput, IFilePreviewModalOutput } from "@app/shared/modals/model/action-modal";
import { ModalService } from "@app/shared/modals/services/modal.service";
import { IApiFile } from "@bssh/comp-lib";
import { V1pre3RunCompact } from "@bssh/ng-sdk/api/models/v1pre-3run-compact";
import environment from "@environments/environment";
import { AnalysisStatus } from '@app/core/model/appSessions/analysis-status';
import _ from "lodash";

export interface IAnalysisUtilityService {
    isMultiNode(appSession: IAppSession): boolean;
    isWorkflow(appSession: IAppSession): boolean;
}

@Injectable({
    providedIn: 'root'
  })
  
  export class AnalysisUtilityService  extends BaseService implements IAnalysisUtilityService  {
   
    constructor(private propertyService : PropertyService, 
        private modalService: ModalService) { super()}

    public isMultiNode(appSession: IAppSession): boolean {
        if (appSession.Properties && appSession.Properties.Items) {
            const multiNodeProp = this.propertyService.getPropertyValue(
                appSession.Properties.Items, "BaseSpace.Private.IsMultiNode") as string;
            return (_.isString(multiNodeProp) && multiNodeProp === 'True');
        }
        return false;
    }

    /**
     * Instrument platform of the run this appsession was launched from, e.g. "NovaSeqXSeries", or null
     * when the response carries nothing to derive it from. Used to resolve instrument-scoped code
     * features (e.g. OlaHtmlApplicationReports), which are keyed by platform rather than by
     * instrument type, so the run's type is mapped through ToPlatform instead of being used directly.
     * Callers treat null as "not enabled".
     */
    public getInputRunInstrumentPlatform(appSession: IAppSession): string {
        if (appSession.Properties && appSession.Properties.Items) {
            const inputRuns = this.propertyService.getPropertyValue(
                appSession.Properties.Items, PropertyName.INPUT_RUNS) as V1pre3RunCompact[];

            if (inputRuns && inputRuns.length > 0) {
                // ToPlatform reports "Unknown" for a missing or unrecognized instrument type. No code
                // feature is ever scoped to it, so report null rather than a platform that can never match.
                const instrumentPlatform = InstrumentTypes.ToPlatform(inputRuns[0].InstrumentType);
                return instrumentPlatform === InstrumentPlatforms.UNKNOWN ? null : instrumentPlatform;
            }
        }
        return null;
    }

    public isAnalysisFilesRoute(route: string): boolean {
      return /^\/analyses\/\d+\/files(\/.*)?$/.test(route);
    }

    public isAnalysisInputFormDataAvailable(analysis: IAppSession): boolean {
      return analysis.ExecutionStatus !== AnalysisStatus.Initializing;
    }

    public isWorkflow(appSession: IAppSession): boolean {
        return appSession.Application && appSession.Application.Category === ApplicationCategory.WORKFLOW;
    }

    showFilePreview(fileToPreview: IApiFile, isPreviewable: boolean = false, isFolderLinkEnabled: boolean = false) {
      
        const data: IFilePreviewModalInput = {
          apiBaseUrl: `${StringUtilities.trimTrailingSlash(environment.apiUrl)}/`,
          requestedFile: fileToPreview,
          isFolderLinkEnabled: isFolderLinkEnabled,
          isPreviewableTextFile: isPreviewable
        };

        this.subs.sink = this.modalService.openModal(data, FilePreviewModalComponent).confirm.subscribe({
          next: (output: IFilePreviewModalOutput) => {
            if (output) {
              this.downloadFile(output.downloadLink, output.fileName);
            }
          },
          error: error => { console.error(error); /* TODO: handle */ }
        });
      }
    
      downloadFile(link: string, fileName: string): void {
        const element = document.createElement('a');
        element.setAttribute('href', link);
        element.setAttribute('download', fileName);
    
        element.style.display = 'none';
        document.body.appendChild(element);
    
        element.click();
    
        document.body.removeChild(element);
      }
  }
